import React from "react";
import { Alert, Button, Dialog, Spinner } from "@/components/ui";
import { SharedPostFlowDefaults } from "@/components";
import { PostFlowProvider, usePostFlowContext } from "../SharedPostFlowContext";
import { PostPlayerRequest } from "@/interfaces";
import { PostServices } from "@/services";
import { useSocialCoachSession } from "@/utils";
import { useQueryClient } from "@tanstack/react-query";
import {
  CaptionCoachStep,
  DeliveryOptionsStep,
  EditPostStep,
  MentionsStep,
  SuccessStep,
} from "@/components/PostDialogs/PostFlowComonents";
import { AlertCircle } from "lucide-react";

enum Step {
  EDIT = 1,
  CAPTION_COACH = 2,
  MENTIONS = 3,
  DELIVERY = 4,
  SUCCESS = 5,
}

interface Props {
  open: boolean;
  handleClose: () => void;

  playerId?: number;
  postId?: number | string;
  defaults: SharedPostFlowDefaults;
}

export function PlayerPostDialog(props: Props) {
  return (
    <PostFlowProvider defaults={props.defaults} key={props.defaults.promptId || "new"}>
      <PlayerPostDialogContent {...props} />
    </PostFlowProvider>
  );
}

function PlayerPostDialogContent({ open, handleClose, postId, playerId }: Props) {
  const queryClient = useQueryClient();
  const { playerId: sessionPlayerId } = useSocialCoachSession();
  const effectivePlayerId = playerId || sessionPlayerId;

  const [currentStep, setCurrentStep] = React.useState<Step>(Step.EDIT);
  const {
    promptId,
    deliveryOptions,
    buildPostRequest,
    validatePostRequest,
    blockedWords,
    isDeliveryStepIncomplete,
    isPosting,
    postButtonLabel,
    setIsPosting,
    setPostError,
    pendingCaption,
    setPendingCaption,
    setDefaultMessage,
    setHashtags,
    postError,
  } = usePostFlowContext();

  const handleConfirmPost = async (): Promise<any> => {
    try {
      setIsPosting(true);
      setPostError(undefined);
      const basePostRequest = buildPostRequest();
      if (!validatePostRequest(basePostRequest)) return;

      if (basePostRequest.mediaGroup?.mediaGroupId) basePostRequest.mediaGroup.mediaGroupId = undefined;

      const postRequest: PostPlayerRequest = {
        ...basePostRequest,
        playerId: effectivePlayerId,
        promptId: promptId,
        postId: postId,
      };

      await PostServices.createAsPlayer(postRequest);
      queryClient.invalidateQueries({ queryKey: ["calendarEvents"] });
      setCurrentStep(Step.SUCCESS);
    } catch (e: any) {
      setPostError(e?.message || "Unable to complete your post. Please try again later.");
    } finally {
      setIsPosting(false);
    }
  };

  const stepConfigs: Record<
    Step,
    {
      title: string;
      nextLabel?: string;
      isValid?: boolean;
      onNext?: () => void;
      onBack?: () => void;
    }
  > = {
    [Step.EDIT]: {
      title: "Review Caption",
      isValid: blockedWords.length === 0,
      onNext: () => setCurrentStep(Step.DELIVERY),
    },
    [Step.CAPTION_COACH]: {
      title: "Generate Caption",
      nextLabel: "Apply Caption",
      isValid: !!pendingCaption && blockedWords.length === 0,
      onNext: () => {
        if (pendingCaption) {
          const hashtags = pendingCaption.match(/#[\w-]+/g) || [];
          const caption = pendingCaption.replace(/\s?#[\w-]+/g, "").trim();
          setDefaultMessage(caption);
          setHashtags(hashtags.join(" "));
          setPendingCaption(null);
        }
        setCurrentStep(Step.EDIT);
      },
      onBack: () => {
        setPendingCaption(null);
        setCurrentStep(Step.EDIT);
      },
    },
    [Step.MENTIONS]: {
      title: "Custom Mentions (Beta)",
      isValid: true,
      onNext: () => setCurrentStep(Step.EDIT),
      onBack: () => setCurrentStep(Step.EDIT),
    },
    [Step.DELIVERY]: {
      title: "Delivery Options",
      isValid: !isDeliveryStepIncomplete,
      onNext: () => setCurrentStep(Step.SUCCESS),
      onBack: () => setCurrentStep(Step.EDIT),
    },
    [Step.SUCCESS]: {
      title: "Success!",
    },
  };

  const currentConfig = stepConfigs[currentStep];

  const renderStepContent = () => {
    switch (currentStep) {
      case Step.EDIT:
        return (
          <EditPostStep
            handleCaptionCoach={() => setCurrentStep(Step.CAPTION_COACH)}
            handleMentions={() => setCurrentStep(Step.MENTIONS)}
          />
        );
      case Step.CAPTION_COACH:
        return <CaptionCoachStep />;
      case Step.DELIVERY:
        return <DeliveryOptionsStep />;
      case Step.MENTIONS:
        return <MentionsStep />;
      case Step.SUCCESS:
        return <SuccessStep />;
      default:
        return null;
    }
  };

  return (
    <Dialog open={open} onOpenChange={open => !open && handleClose()} size={"2xl"}>
      <Dialog.Header>
        <Dialog.Title>
          <div className={"flex flex-row"}>
            <div className={"border-r border-slate-300 pr-4 mr-4"}>
              {deliveryOptions.isAutoPost ? "New Post" : "Send Prompt"}
            </div>
            <div className="font-medium">{currentConfig.title}</div>
          </div>
        </Dialog.Title>
      </Dialog.Header>
      <Dialog.Content className="pb-6 max-h-[70vh] overflow-y-auto flex flex-col gap-4">
        {postError && (
          <Alert variant="destructive">
            <AlertCircle />
            <Alert.Title>Error submitting post</Alert.Title>
            <Alert.Description>{postError}</Alert.Description>
          </Alert>
        )}
        {renderStepContent()}
      </Dialog.Content>
      <Dialog.Footer>
        <div className="flex flex-row justify-between gap-2">
          <div>
            {currentStep === Step.EDIT && (
              <Button variant="ghost" onClick={handleClose}>
                Discard
              </Button>
            )}
          </div>
          <div>
            {currentStep === Step.SUCCESS ? (
              <Button onClick={() => handleClose()}>Done</Button>
            ) : (
              <div className="flex flex-row justify-end gap-2">
                {currentConfig.onBack && (
                  <Button variant="outline" onClick={currentConfig.onBack} disabled={isPosting}>
                    Back
                  </Button>
                )}

                {currentStep < Step.DELIVERY ? (
                  <Button onClick={currentConfig.onNext} disabled={currentConfig.isValid === false}>
                    {currentConfig.nextLabel || "Next"}
                  </Button>
                ) : (
                  <Button onClick={handleConfirmPost} disabled={isPosting} className="min-w-[140px]">
                    {isPosting ? (
                      <>
                        <Spinner className="mr-2 size-4" />
                        Submitting...
                      </>
                    ) : (
                      postButtonLabel
                    )}
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>
      </Dialog.Footer>
    </Dialog>
  );
}
